/* eslint-disable */
// src/contexts/AppConfigContext.jsx

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, ensureFirebase } from "../services/firebase";

ensureFirebase();

/**
 * AppConfig 전체 타입 (지금은 splash만 실제 사용, 나머지는 이후에 채울 예정)
 */
const DEFAULT_APP_CONFIG = {
    splash: {
        img: "",
    },
    home: {
        hero: null,
        quickActions: [],
        sections: [],
        theme: {},
    },
    login: {
        background: { img: "" },
        buttons: [],
    },
    detailScreens: {
        // franchiseApply / educationCenter / ... 등은 페이지에서 필요할 때 꺼내 쓸 거라 여기선 껍데기만
    },
    access: {
        education: {
            enabled: false,
            message: "",
        },
        mySales: {
            message: "",
        },
    },
};


const AppConfigContext = createContext({
    config: DEFAULT_APP_CONFIG,
    loading: true,
    refresh: async () => { },
});

const LOCAL_KEY_SPLASH = "dhm.appConfig.splash";

/* 로컬 캐시에서 스플래시 설정 가져오기 */
function loadSplashFromLocal() {
    try {
        const raw = localStorage.getItem(LOCAL_KEY_SPLASH);
        if (!raw) return DEFAULT_APP_CONFIG.splash;
        const parsed = JSON.parse(raw);
        return {
            img: typeof parsed.img === "string" ? parsed.img : "",
        };
    } catch {
        return DEFAULT_APP_CONFIG.splash;
    }
}

/* 스플래시 설정을 로컬 캐시에 저장 */
function saveSplashToLocal(splashConfig) {
    try {
        const payload = {
            img: splashConfig?.img || "",
        };
        localStorage.setItem(LOCAL_KEY_SPLASH, JSON.stringify(payload));
    } catch {
        // 캐시 실패는 무시
    }
}

/* Firestore에서 스플래시 설정 가져오기 */
async function fetchSplashFromServer() {
    try {
        const ref = doc(db, "ui_splash", "published");
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        // 우리가 Admin에서 저장한 구조: { splash: { img: "..." }, _meta: ... } 형태라고 가정
        const splash = data.splash || {};
        return {
            img: splash.img || "",
        };
    } catch (e) {
        console.error("[AppConfig] fetchSplashFromServer error:", e);
        return null;
    }
}

// 홈 화면 설정
async function fetchHomeFromServer() {
    try {
        // homeConfigService에서 쓰는 컬렉션 키와 맞춰야 함 (지금은 ui_home_config 가정)
        const ref = doc(db, "home_config", "published");
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        const { _meta, ...config } = data;
        return config;
    } catch (e) {
        console.error("[AppConfig] fetchHomeFromServer error:", e);
        return null;
    }
}

// 로그인 화면 설정
async function fetchLoginFromServer() {
    try {
        const ref = doc(db, "ui_login", "published");
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        const { _meta, ...config } = data;
        return config;
    } catch (e) {
        console.error("[AppConfig] fetchLoginFromServer error:", e);
        return null;
    }
}

// 상세 화면 설정 (가맹가입/교육센터/쇼핑몰/공지/고객지원/내정보 등)
async function fetchDetailScreensFromServer() {
    try {
        const ref = doc(db, "ui_detail_screens", "published");
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        const { _meta, ...config } = data;
        return config;
    } catch (e) {
        console.error("[AppConfig] fetchDetailScreensFromServer error:", e);
        return null;
    }
}

// 접근 관리 설정 (교육센터 가드, 내 매출 보기 메시지)
async function fetchAccessControlFromServer() {
    try {
        const ref = doc(db, "ui_access_control", "published");
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        const { _meta, ...config } = data;
        return config;
    } catch (e) {
        console.error("[AppConfig] fetchAccessControlFromServer error:", e);
        return null;
    }
}


export function AppConfigProvider({ children }) {
    // 1) 초기는 항상 로컬 캐시 기준
    const localSplash = useMemo(() => loadSplashFromLocal(), []);
    const [config, setConfig] = useState({
        ...DEFAULT_APP_CONFIG,
        splash: localSplash,
    });
    const [loading, setLoading] = useState(true);

    // 공용 리프레시 함수
    const refresh = async () => {
        setLoading(true);
        try {
            const [
                splashFromServer,
                homeFromServer,
                loginFromServer,
                detailFromServer,
                accessFromServer,
            ] = await Promise.all([
                fetchSplashFromServer(),
                fetchHomeFromServer(),
                fetchLoginFromServer(),
                fetchDetailScreensFromServer(),
                fetchAccessControlFromServer(),
            ]);

            setConfig((prev) => {
                const nextSplash =
                    splashFromServer || prev.splash || DEFAULT_APP_CONFIG.splash;

                // 스플래시는 로컬 캐시에도 저장
                saveSplashToLocal(nextSplash);

                return {
                    ...prev,
                    splash: nextSplash,
                    home: homeFromServer || prev.home || DEFAULT_APP_CONFIG.home,
                    login: loginFromServer || prev.login || DEFAULT_APP_CONFIG.login,
                    detailScreens:
                        detailFromServer ||
                        prev.detailScreens ||
                        DEFAULT_APP_CONFIG.detailScreens,
                    access:
                        accessFromServer || prev.access || DEFAULT_APP_CONFIG.access,
                };
            });
        } finally {
            setLoading(false);
        }
    };


    // 마운트 시 한 번 서버에서 최신값 패치
    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            config,
            loading,
            refresh,
        }),
        [config, loading]
    );

    return (
        <AppConfigContext.Provider value={value}>
            {children}
        </AppConfigContext.Provider>
    );
}

/* 페이지/컴포넌트에서 쓰는 훅 */
export function useAppConfig() {
    return useContext(AppConfigContext);
}
